"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.updateTypeOrCategoryByIdAction = exports.reducer = exports.getTypesFromCategory = exports.getIssueTypeEntitiesMap = exports.getIssueTypeCategories = exports.getIsLoadingIssueTypes = exports.getFallbackDefaultType = exports.getAllGroupedTypes = exports.getActiveGroupedTypes = exports.fetchIssueTypesAction = exports.deleteTypeOrCategoryByIdAction = exports.STATE_KEY = void 0;
var _toolkit = require("@reduxjs/toolkit");
var _map = _interopRequireDefault(require("lodash/map"));
var _fetchHelpers = require("../fetchHelpers");
var _sortingHelpers = require("../../../utils/data/sortingHelpers");
var _issueTypeUtils = require("../../../utils/data/issueTypeUtils");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
const getTypesFromCategory = (flattenedCategory, issueTypeEntitiesMap) => flattenedCategory.childrenIds.map(categoryId => issueTypeEntitiesMap[categoryId]).filter(type => !!type);
exports.getTypesFromCategory = getTypesFromCategory;
const initialState = {
  [_fetchHelpers.STATUS_KEY]: _fetchHelpers.FetchStatus.Initial,
  entitiesMap: {},
  issueCategoryIds: [],
  pendingUpdatesMap: {},
  pendingUpdateCount: 0
};
const issueTypesSlice = (0, _toolkit.createSlice)({
  name: "issueTypes",
  initialState,
  reducers: {
    issueTypesFetchStart(state) {
      state[_fetchHelpers.STATUS_KEY] = _fetchHelpers.FetchStatus.Loading;
    },
    issueTypesFetchSuccess(state, action) {
      state[_fetchHelpers.STATUS_KEY] = _fetchHelpers.FetchStatus.Success;
      state.issueCategoryIds = (0, _map.default)(action.payload, "id");
      state.entitiesMap = (0, _issueTypeUtils.flattenIssueTypes)(action.payload.sort(_sortingHelpers.localeCompareByTitle));
      // For updating issues:
      // we fetch all issue types to update type data after updates, and
      // since this will be returning the most up-to-date data we can
      // safely clear the pending updates when there are no more in-flight updates.
      if (state.pendingUpdateCount === 0) {
        state.pendingUpdatesMap = {};
      }
    },
    issueTypeOrCategoryUpdateByIdStart: (state, action) => {
      state.pendingUpdateCount += 1;
      state.pendingUpdatesMap = {
        ...state.pendingUpdatesMap,
        [action.payload.id]: action.payload.entityChanges
      };
    },
    issueTypeOrCategoryUpdateByIdSuccess: state => {
      state.pendingUpdateCount -= 1;
    },
    issueTypeOrCategoryUpdateByIdFailure: (state, action) => {
      state.pendingUpdateCount -= 1;
      delete state.pendingUpdatesMap[action.payload.id];
    }
  }
});
const {
  issueTypesFetchStart,
  issueTypesFetchSuccess,
  issueTypeOrCategoryUpdateByIdStart,
  issueTypeOrCategoryUpdateByIdSuccess,
  issueTypeOrCategoryUpdateByIdFailure
} = issueTypesSlice.actions;
const fetchIssueTypesAction = () => async (dispatch, getState, _ref) => {
  let {
    sdk
  } = _ref;
  dispatch(issueTypesFetchStart());
  await sdk.IssueTypes.getAll().then(payload => {
    dispatch(issueTypesFetchSuccess(payload.results));
  });
  // @TODO we don't have any behavior for if this fails, and perhaps we should
};
exports.fetchIssueTypesAction = fetchIssueTypesAction;
const reducer = exports.reducer = issueTypesSlice.reducer;
const STATE_KEY = exports.STATE_KEY = issueTypesSlice.name;
const getIssueTypesState = state => {
  return state[STATE_KEY];
};

// getIssueTypeEntitiesMap will include any optimistic updates
const getIssueTypeEntitiesMap = exports.getIssueTypeEntitiesMap = (0, _toolkit.createSelector)(getIssueTypesState, issueTypesState => {
  const entitiesMap = {
    ...issueTypesState.entitiesMap
  };
  Object.keys(issueTypesState.pendingUpdatesMap).forEach(entityId => {
    entitiesMap[entityId] = {
      ...entitiesMap[entityId],
      ...issueTypesState.pendingUpdatesMap[entityId]
    };
  });
  return entitiesMap;
});
const getIsLoadingIssueTypes = exports.getIsLoadingIssueTypes = (0, _toolkit.createSelector)(getIssueTypesState, _fetchHelpers.isLoading);

/**
 * returns just the issue type categories, not the types
 */
const getIssueTypeCategories = exports.getIssueTypeCategories = (0, _toolkit.createSelector)(state => getIssueTypesState(state).issueCategoryIds, getIssueTypeEntitiesMap, (orderedCategoryIds, issueTypeEntitiesMap) => {
  return orderedCategoryIds.filter(categoryId => {
    const category = issueTypeEntitiesMap[categoryId];
    // it's probably fair to assume that `orderedCategoryIds`
    // are always category ids, but for the type checker
    // it's just as well to be absolutely sure that the result of this is a category
    return category && (0, _issueTypeUtils.isCategory)(category);
  }).map(issueTypeId => {
    return issueTypeEntitiesMap[issueTypeId];
  }).sort(_sortingHelpers.localeCompareByTitle);
});
const getAllGroupedTypes = exports.getAllGroupedTypes = (0, _toolkit.createSelector)(getIssueTypeCategories, getIssueTypeEntitiesMap, (issueTypeCategories, issueEntitiesMap) => {
  return issueTypeCategories.map(category => ({
    ...category,
    types: getTypesFromCategory(category, issueEntitiesMap)
  })).filter(category => category.types.length > 0);
});
const getActiveGroupedTypes = exports.getActiveGroupedTypes = (0, _toolkit.createSelector)(getIssueTypeCategories, getIssueTypeEntitiesMap, (issueTypeCategories, issueEntitiesMap) => {
  return issueTypeCategories.filter(category => category.isActive).map(category => ({
    ...category,
    types: getTypesFromCategory(category, issueEntitiesMap).filter(issueType => issueType.isActive)
  })).filter(category => category.types.length > 0);
});

/**
 * We want to select "General" by default, but this doesn't exist for now,
 * so we'll choose the first type instead. If the first one doesn't exist,
 * return an empty object so that if any fields are called, they are undefined
 */
const getFallbackDefaultType = exports.getFallbackDefaultType = (0, _toolkit.createSelector)(getIssueTypeCategories, getIssueTypeEntitiesMap, (issueTypeCategories, issueEntitiesMap) => {
  const firstActiveIssueTypeCategory = issueTypeCategories.find(issueType => issueType.isActive);
  if (firstActiveIssueTypeCategory) {
    return getTypesFromCategory(firstActiveIssueTypeCategory, issueEntitiesMap).find(issueType => issueType.isActive);
  }
  return undefined;
});
const updateTypeOrCategoryByIdAction = _ref2 => {
  let {
    id,
    entityChanges,
    isCategory,
    onSuccess,
    onFailure
  } = _ref2;
  return async (dispatch, getState, _ref3) => {
    let {
      sdk
    } = _ref3;
    dispatch(issueTypeOrCategoryUpdateByIdStart({
      id,
      entityChanges
    }));
    const sdkUpdateFn = isCategory ? sdk.IssueTypes.Categories.update : sdk.IssueTypes.update;
    try {
      await sdkUpdateFn(id, entityChanges);
      dispatch(issueTypeOrCategoryUpdateByIdSuccess());
    } catch (error) {
      dispatch(issueTypeOrCategoryUpdateByIdFailure({
        id
      }));
      onFailure && onFailure(error);
      return;
    }
    onSuccess && onSuccess();
    // instead of processing each change individually,
    // we make sure to fetch all types + categories after every change.
    // This reduces the risk of stale data, and ensures
    // that reordering (and scrolling to the correct row)
    // occurs on a title change, or the type is correctly
    // moved to the new category on a category change.
    // The success of this action clears the pending updates.
    dispatch(fetchIssueTypesAction());
  };
};
exports.updateTypeOrCategoryByIdAction = updateTypeOrCategoryByIdAction;
const deleteTypeOrCategoryByIdAction = _ref4 => {
  let {
    id,
    isCategory,
    onSuccess,
    onFailure
  } = _ref4;
  return async (dispatch, getState, _ref5) => {
    let {
      sdk
    } = _ref5;
    const sdkDeleteFn = isCategory ? sdk.IssueTypes.Categories.delete : sdk.IssueTypes.delete;
    try {
      await sdkDeleteFn(id);
      onSuccess && onSuccess();
    } catch (error) {
      onFailure && onFailure(error);
      return;
    }
    // using same pattern as for updates: fetch everything again after deletion
    dispatch(fetchIssueTypesAction());
  };
};
exports.deleteTypeOrCategoryByIdAction = deleteTypeOrCategoryByIdAction;
//# sourceMappingURL=issueTypes.js.map